import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2, Lock, ShieldAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { serverMessage } from "@/lib/apiError";
import { formatUyMinor } from "@/lib/formatUyMinor";
import { FiscalDeclarationPrompt } from "@/components/features/notarial-requests/fiscal-declaration-prompt";
import {
  getConsumerCfeObligation,
  recordConsumerCfe,
  type ConsumerCfeObligation,
  type RfqGateBlocker,
} from "@/services/professionalEarningsService";

/**
 * The blocking gate on an RFQ job whose professional has not recorded the CFE
 * they issued to the consumer (spec 300908fc slice 3b, D3).
 *
 * NOT DISMISSIBLE, and that is the requirement rather than an oversight. Every
 * write behind this page is refused server-side until the CFE is on file, so a
 * modal the professional could close would only let them discover that one
 * failed action at a time. There is no close button, no overlay click-through
 * and no Escape handler.
 *
 * DELIBERATELY NOT the `Dialog` primitive. Its `DialogContent` ships a
 * hardcoded close button and Radix dismisses on Escape and outside-press by
 * default; suppressing all three would be fighting the component rather than
 * using it, and a future upgrade could quietly hand the escape hatch back.
 *
 * THE PAGE BEHIND IT STAYS READABLE, which is the whole reason this is an
 * overlay and not a redirect: the professional needs the job's details in
 * front of them to issue a correct CFE for it. The gate stops them acting, not
 * looking.
 *
 * THE FISCAL DECLARATION COMES FIRST when it is also outstanding — a
 * professional with no RUT on file cannot issue the document this modal is
 * asking for. That branch is defensive and expected never to fire: the
 * backfill prompt after a first quote (task 8f2083b) is what keeps it from
 * happening, and award-time capability gating (task 43ef29e) is what makes it
 * impossible.
 */
export function ConsumerCfeGateModal({
  legId,
  blockers,
  onResolved,
}: {
  legId: string;
  blockers: RfqGateBlocker[];
  /** Called once the CFE is on file, so the caller can refetch the gate. */
  onResolved: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [obligation, setObligation] = useState<ConsumerCfeObligation | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [total, setTotal] = useState("");
  const [saving, setSaving] = useState(false);
  const [fiscalDone, setFiscalDone] = useState(false);

  const needsFiscal = blockers.includes("fiscal_declaration") && !fiscalDone;

  /**
   * The expected total comes from the SERVER, never computed here.
   *
   * It is the same figure the write path enforces to the centésimo. A total
   * derived on the page would eventually disagree with the one the endpoint
   * accepts, and the professional would be told to invoice an amount that is
   * then refused.
   */
  useEffect(() => {
    let cancelled = false;
    void getConsumerCfeObligation(legId)
      .then((o) => {
        if (cancelled) return;
        setObligation(o);
        // Prefilled, because the amount is not the professional's to choose:
        // it is what the consumer already paid. Still editable, so the field
        // can disagree with the document in front of them and be caught.
        setTotal((o.expectedTotalMinor / 100).toFixed(2));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [legId]);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!file) {
      toast.error(t("requests.consumerCfeGate.fileRequired"));
      return;
    }
    const parsed = Number(total.replace(",", "."));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error(t("requests.consumerCfeGate.totalRequired"));
      return;
    }
    setSaving(true);
    try {
      await recordConsumerCfe(legId, {
        declaredTotalMinor: Math.round(parsed * 100),
        file,
      });
      toast.success(t("requests.consumerCfeGate.savedToast"));
      onResolved();
    } catch (err) {
      // serverMessage surfaces the 400's own body, which on a total mismatch
      // NAMES the figure to use. "El total no coincide" would be unactionable;
      // the amount is the only part of the refusal they can act on.
      //
      // The `??` is load-bearing rather than defensive habit: serverMessage
      // returns null for anything that is not an Axios response — a dropped
      // connection, a CORS failure — and `toast.error(null)` renders an empty
      // toast. Same shape as FiscalDeclarationPrompt, which is the pattern
      // this file should have followed from the start.
      toast.error(serverMessage(err) ?? t("requests.consumerCfeGate.errorGeneric"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="consumer-cfe-gate-title"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm sm:items-center sm:p-6"
    >
      <div className="my-auto w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900 sm:p-8">
        <div className="mb-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
          <Lock className="h-3.5 w-3.5" aria-hidden />
          {t("requests.consumerCfeGate.eyebrow")}
        </div>

        {needsFiscal ? (
          <FiscalFirstStep onSaved={() => setFiscalDone(true)} />
        ) : (
          <>
            <h2
              id="consumer-cfe-gate-title"
              className="text-xl font-semibold text-slate-900 dark:text-slate-50"
            >
              {t("requests.consumerCfeGate.title")}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              {t("requests.consumerCfeGate.lead")}
            </p>

            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
              <div className="flex items-start gap-2">
                <ShieldAlert
                  className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-500 dark:text-slate-400"
                  aria-hidden
                />
                <div>
                  <p className="text-xs font-semibold text-slate-900 dark:text-slate-100">
                    {t("requests.consumerCfeGate.whyTitle")}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                    {t("requests.consumerCfeGate.whyBody")}
                  </p>
                </div>
              </div>
            </div>

            {/* THE FIGURE, as prominent as the form. The failure this gate
                exists to catch is a professional invoicing the amount they
                quoted — an honest mistake that leaves the consumer's document
                short by exactly the platform's fee, looking correct the whole
                way. Telling them the number before they type is worth more
                than refusing them after. */}
            {obligation ? (
              <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950/40">
                <p className="text-xs font-medium text-emerald-800 dark:text-emerald-300">
                  {t("requests.consumerCfeGate.expectedLabel")}
                </p>
                <p className="mt-1 text-2xl font-semibold tabular-nums text-emerald-900 dark:text-emerald-200">
                  {formatUyMinor(obligation.expectedTotalMinor, obligation.currency)}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-emerald-800/80 dark:text-emerald-300/80">
                  {t("requests.consumerCfeGate.expectedHint")}
                </p>
              </div>
            ) : (
              <div className="mt-5 flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              </div>
            )}

            <form onSubmit={(e) => void handleSubmit(e)} className="mt-5 space-y-4">
              <div>
                <Label htmlFor="cfe-file">
                  {t("requests.consumerCfeGate.fileLabel")}
                </Label>
                <Input
                  id="cfe-file"
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="cfe-total">
                  {t("requests.consumerCfeGate.totalLabel")}
                </Label>
                <Input
                  id="cfe-total"
                  inputMode="decimal"
                  value={total}
                  onChange={(e) => setTotal(e.target.value)}
                  className="mt-1 tabular-nums"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {t("requests.consumerCfeGate.totalHint")}
                </p>
              </div>
              {/* RIGHT-ALIGNED, like every other submit in this flow. It was
                  w-full, which made a commit button look like one more field
                  in the stack it sits under. */}
              <div className="flex justify-end">
                <Button type="submit" disabled={saving || !obligation}>
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                      {t("requests.consumerCfeGate.submitting")}
                    </>
                  ) : (
                    t("requests.consumerCfeGate.submit")
                  )}
                </Button>
              </div>
            </form>
          </>
        )}

        <p className="mt-4 text-center text-xs text-slate-400 dark:text-slate-500">
          {t("requests.consumerCfeGate.locked")}
        </p>
      </div>
    </div>
  );
}

/**
 * The declaration, when it is also outstanding.
 *
 * Its own component because it shares nothing with the CFE form but the modal
 * shell, and holding both flows in one function was the nesting the complexity
 * check was measuring.
 *
 * The form itself is REUSED rather than reimplemented: it is the same question
 * asked at a different moment, and the copy already lives in the shared
 * `auth.professionalFiscal` namespace precisely so two wordings cannot drift.
 */
function FiscalFirstStep({ onSaved }: { onSaved: () => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <h2
        id="consumer-cfe-gate-title"
        className="text-xl font-semibold text-slate-900 dark:text-slate-50"
      >
        {t("requests.consumerCfeGate.fiscalFirstTitle")}
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
        {t("requests.consumerCfeGate.fiscalFirstLead")}
      </p>
      <div className="mt-5">
        {/* NOT "Guardar y enviar presupuesto" here. This professional sent
            their quote days ago and WON it — offering to send it again
            describes work that is behind them. The prompt's default label is
            true only where it first appeared, interrupting someone mid-quote. */}
        <FiscalDeclarationPrompt
          onSaved={onSaved}
          submitLabel={t("requests.consumerCfeGate.fiscalFirstSubmit")}
        />
      </div>
    </>
  );
}
